// ClienteAuthContext — sesión de cliente (propietario), separada del
// AuthContext de ejecutivos.
//
// Mientras AuthContext usa Supabase Auth (email + password, gestionado por
// la lib supabase), ClienteAuthContext usa un JWT custom que viajamos a
// nuestros propios endpoints (/api/cliente/*). El JWT vive en localStorage.

import { createContext, useContext, useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'vera_cliente_session';

const ClienteAuthContext = createContext(null);

function readSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Validez mínima del shape
    if (!parsed.token || !parsed.cliente?.id) return null;
    if (parsed.expiresAt && parsed.expiresAt < Date.now()) {
      // Sesión expirada → limpia
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeSession(session) {
  if (session) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

export function ClienteAuthProvider({ children }) {
  const [session, setSession] = useState(() => readSession());
  const [loading, setLoading] = useState(false);

  // Si la sesión cambia en otra pestaña, sincronizamos
  useEffect(() => {
    function onStorage(e) {
      if (e.key === STORAGE_KEY) {
        setSession(readSession());
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Llamado desde ClienteAuthCallback tras consumir el magic link
  const setSesion = useCallback((sessionToken, cliente, expiresInSeconds) => {
    const expiresAt = Date.now() + (expiresInSeconds * 1000);
    const next = { token: sessionToken, cliente, expiresAt };
    writeSession(next);
    setSession(next);
  }, []);

  const logout = useCallback(() => {
    writeSession(null);
    setSession(null);
  }, []);

  // Refresca campos del cliente en la sesión actual sin renovar el JWT.
  // Usado por flujos como onboarding (set onboarded_at en local tras POST ok).
  const updateCliente = useCallback((patch) => {
    setSession(prev => {
      if (!prev) return prev;
      const next = { ...prev, cliente: { ...prev.cliente, ...patch } };
      writeSession(next);
      return next;
    });
  }, []);

  // Helper para hacer fetch autenticado a /api/cliente/*
  const fetchAuth = useCallback(async (path, opts = {}) => {
    if (!session?.token) throw new Error('Sin sesión');
    const headers = {
      ...(opts.headers || {}),
      Authorization: `Bearer ${session.token}`,
      'Content-Type': 'application/json',
    };
    const res = await fetch(path, { ...opts, headers });
    if (res.status === 401) {
      // Token rechazado → cerramos sesión
      logout();
      throw new Error('Sesión expirada');
    }
    return res;
  }, [session?.token, logout]);

  const value = {
    session,
    cliente: session?.cliente ?? null,
    isAuthenticated: !!session,
    loading,
    setSesion,
    logout,
    updateCliente,
    fetchAuth,
    setLoading,
  };

  return (
    <ClienteAuthContext.Provider value={value}>
      {children}
    </ClienteAuthContext.Provider>
  );
}

export const useClienteAuth = () => {
  const ctx = useContext(ClienteAuthContext);
  if (!ctx) throw new Error('useClienteAuth fuera de ClienteAuthProvider');
  return ctx;
};
